// 2026-09-22 신규 — 네이버 클립 수익 제출 바텀 시트 (시안 A · 대표 결정 2026-09-22 · 기획 docs/ops/naver-revenue-submission-plan.md).
// 단계: 안내 → 파일 선택·업로드·자동 읽기(lib uploadAndRead) → 읽은 결과 확인(맞아요 / 달라요 → 직접 입력) → 완료.
// 2026-10-08 양식 강제(대표 지시): 자동 읽기가 돌려준 화면 종류·줄 상태로 「받을 수 없는 캡처」(다른 화면 · 확정 전 「지급 예정」 · 해당 달 줄 없음)는
//   그 자리에서 돌려보내고(step=bad) 다른 캡처를 받는다 — 서버 naver_revenue_submit 도 같은 조건으로 거부한다(R9). 5만원 미만 이월 달도 그 달 금액 그대로(2026-10-10 제이 의견) — 결과 단계에 carriedInfo 한 줄, 다음 달 합산은 서버 R10.
// 자동 읽기 자체가 실패하면(종류를 모름) 종전대로 금액 직접 입력(edited=true, 사유 「자동 읽기 실패」 → R8, 운영팀이 캡처를 본다). 반려(rejected) 카드도 같은 흐름.
// 🔒 검사 규칙 이름(R1~R9)·단가·중앙값은 여기 절대 쓰지 않는다 — 제출 뒤엔 「운영팀 확인」 한 줄뿐.
// 2026-10-13 3단계(대표 결정 시안 1 + 시안 3 · B안): ① 상황 고르기(ask) → ② 찍을 화면·체크리스트(shoot) → ③ 올리고 확인(reading·bad·result).
//   「지급 예정」을 고르면 올리지 않고 기다림(wait). PC 에서는 ②에 「휴대폰으로 이어서 하기」 QR(정산 화면 주소 naverRevenueLink).
import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import {
  Box, Flex, Text, Button, Input, Textarea, Image, Spinner,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useToast,
  Checkbox,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import {
  uploadAndRead, submitAmount, validateScreenshotFile, formatKRW, formatViews, monthLabel, creatorCardView, STATUS, LEGAL_ATTEST, formatDeadline,
  normalizeOcrMeta, captureProblem, carriedInfo,
  SITUATION, situationOptions, situationChecklist, naverRevenueLink, publicWebOrigin, naverRowMonth,
} from "../../lib/naverRevenue";
import { tt } from "../../i18n/tt";
import NaverCaptureGuide, { NaverCarriedSample } from "./NaverCaptureGuide";
import NaverRowEquation from "./NaverRowEquation";
import GuideHint from "../guide/GuideHint";

const MAX_AMOUNT = 1_000_000_000;

/** 숫자만 남기고 상한으로 자른다 → 문자열(빈 문자열 허용) */
function normalizeDigits(v) {
  const digits = String(v ?? "").replace(/[^0-9]/g, "");
  if (!digits) return "";
  const n = Math.min(Number(digits), MAX_AMOUNT);
  return String(n);
}
const withCommas = (digits) => (digits ? Number(digits).toLocaleString("ko-KR") : "");

/** OCR 응답은 Edge 함수 규약(amount·period_from·period_to·channel_label). 열 이름(ocr_*)으로 와도 받는다. */
function normalizeOcr(ocr) {
  if (!ocr || typeof ocr !== "object") return null;
  const amount = ocr.amount ?? ocr.ocr_amount ?? null;
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return null;
  return {
    amount: Math.round(Number(amount)),
    periodFrom: ocr.period_from ?? ocr.ocr_period_from ?? null,
    periodTo: ocr.period_to ?? ocr.ocr_period_to ?? null,
    channelLabel: ocr.channel_label ?? ocr.ocr_channel_label ?? null,
  };
}

function fmtDate(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  return tt("a.9d807a3f", "{{v0}}.{{v1}}.{{v2}}", { v0: y, v1: Number(m), v2: Number(day) });
}

function Row({ label, value }) {
  return (
    <Flex gap={3} fontSize="sm" py={1.5} borderBottom="1px solid" borderColor="neutral.100">
      <Text w="56px" flexShrink={0} color="neutral.500">{label}</Text>
      <Text color="neutral.900" fontWeight="600" minW={0} wordBreak="break-all">{value || "-"}</Text>
    </Flex>
  );
}

function AmountInput({ value, onChange, autoFocus }) {
  return (
    <Flex align="center" gap={2}>
      <Input
        inputMode="numeric"
        pattern="[0-9]*"
        value={withCommas(value)}
        onChange={(e) => onChange(normalizeDigits(e.target.value))}
        placeholder="0"
        textAlign="right"
        fontWeight="700"
        fontSize="lg"
        autoFocus={autoFocus}
        aria-label={tt("a.d536e6e9", "네이버 클립 수익 금액")}
      />
      <Text fontSize="sm" color="neutral.600" whiteSpace="nowrap">{tt("a.771dc376", "원")}</Text>
    </Flex>
  );
}

/** 3단계 진행 막대 — ① 상황 ② 찍기 ③ 올리기 */
function StepBar({ at }) {
  return (
    <Flex gap={1} mb={3} aria-label={tt("a.nv13p01", "{{v0}}/3단계", { v0: at })} data-testid="naver-revenue-stepbar" data-step={at}>
      {[1, 2, 3].map((i) => <Box key={i} flex={1} h="4px" borderRadius="2px" bg={i <= at ? "brand.500" : "neutral.200"} />)}
    </Flex>
  );
}

/** 상황에 맞는 그림 한 장 — 이월 달은 안내 카드 + 그래프, 그 밖은 정산 완료 내역 표 */
function SituationFigure({ situation, revenueMonth }) {
  return situation === SITUATION.carried
    ? <NaverCarriedSample revenueMonth={revenueMonth} />
    : <NaverCaptureGuide revenueMonth={revenueMonth} compact />;
}

/** PC 에서만 — 캡처는 휴대폰 네이버 클립 앱에서 찍으므로 휴대폰으로 이어서 하는 QR */
function ContinueOnPhone({ cardId }) {
  const [qr, setQr] = useState("");
  useEffect(() => {
    let cancelled = false;
    const origin = publicWebOrigin(typeof window !== "undefined" ? window.location.origin : "");
    QRCode.toDataURL(origin + naverRevenueLink(cardId), { width: 256, margin: 1, errorCorrectionLevel: "M", color: { dark: "#262524", light: "#ffffff" } })
      .then((u) => { if (!cancelled) setQr(u); })
      .catch(() => { if (!cancelled) setQr(""); });
    return () => { cancelled = true; };
  }, [cardId]);
  return (
    <Flex display={{ base: "none", md: "flex" }} gap={3} align="center" bg="neutral.50" border="1px solid" borderColor="neutral.200" borderRadius="8px" px={3} py={2.5} mt={3} data-testid="naver-revenue-continue-phone">
      {qr && <Image src={qr} alt={tt("a.nv13q01", "휴대폰으로 이어서 하기 QR")} w="72px" h="72px" flexShrink={0} />}
      <Box minW={0}>
        <Text fontSize="xs" fontWeight="800" color="neutral.900">{tt("a.nv13q02", "휴대폰으로 이어서 하기")}</Text>
        <Text fontSize="xs" color="neutral.600" lineHeight="1.55">{tt("a.nv13q03", "캡처는 휴대폰 네이버 클립 앱에서 찍어요. 휴대폰 카메라로 이 QR을 비추면 이 화면이 휴대폰에서 열려요.")}</Text>
      </Box>
    </Flex>
  );
}

/**
 * props: isOpen · card(my_naver_revenue_cards 한 행) · onClose · onSubmitted()
 */
export default function NaverRevenueSheet({ isOpen, card, onClose, onSubmitted }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [step, setStep] = useState("ask"); // ask | wait | shoot | reading | bad | result | done
  const [situation, setSituation] = useState(null); // 상황 고르기 답(SITUATION) — 그림·체크리스트를 고른다
  const [previewUrl, setPreviewUrl] = useState(null);
  const [ocr, setOcr] = useState(null);
  const [readError, setReadError] = useState(null);
  const [problem, setProblem] = useState(null); // 받을 수 없는 캡처 판정(lib captureProblem) — step=bad 에서 보여 준다
  const [carriedNote, setCarriedNote] = useState(null); // 이월된 달 안내(carriedInfo) — 결과 단계에 한 줄
  const [submitInfo, setSubmitInfo] = useState(null); // 제출 응답(carried_prev_amount 등) — 완료 단계에 보여 준다
  const [mode, setMode] = useState("confirm"); // confirm | edit (result 단계)
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attested, setAttested] = useState(false); // 허위 기재 책임 고지 동의 — 제출 필수(서버 p_attested)

  const view = useMemo(() => creatorCardView(card), [card]);
  const isRejected = card?.status === STATUS.rejected;

  // 카드가 바뀌거나 다시 열리면 처음부터
  useEffect(() => {
    if (!isOpen) return;
    setStep("ask");
    setSituation(null);
    setAttested(false);
    setOcr(null);
    setReadError(null);
    setProblem(null);
    setCarriedNote(null);
    setSubmitInfo(null);
    setMode("confirm");
    setAmount("");
    setReason("");
    setSubmitting(false);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [isOpen, card?.id]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !card?.id) return;
    const bad = validateScreenshotFile(file);
    if (bad) {
      toast({ title: bad, status: "warning", duration: 3500, isClosable: true });
      return;
    }
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setStep("reading");
    setCarriedNote(null);
    setAttested(false);
    try {
      const res = await uploadAndRead(supabase, { submissionId: card.id, file });
      // 양식 판정이 먼저 — 금액을 읽었든 못 읽었든 「받을 수 없는 캡처」는 여기서 돌려보낸다(서버도 같은 조건으로 거부)
      const meta = normalizeOcrMeta(res.ocr);
      const prob = captureProblem(meta, card?.revenue_month);
      setProblem(prob);
      if (prob?.block) {
        setOcr(null);
        setReadError(null);
        setStep("bad");
        return;
      }
      const parsed = normalizeOcr(res.ocr);
      // 이월된 달(2026-10-10 제이 의견): 막지 않고 그 달 금액 그대로 받는다 — 안내 한 줄만 붙인다
      setCarriedNote(carriedInfo(meta, card?.revenue_month));
      setOcr(parsed);
      setReadError(parsed ? null : res.error || "ocr failed");
      setAmount(parsed ? String(parsed.amount) : "");
      setMode(parsed ? "confirm" : "edit");
      setStep("result");
    } catch (err) {
      setStep("shoot");
      toast({ title: err?.message || tt("a.5f970215", "업로드에 실패했어요. 다시 시도해 주세요."), status: "error", duration: 4000, isClosable: true });
    }
  };

  const doSubmit = async ({ edited }) => {
    if (!card?.id || submitting) return;
    const n = edited ? Number(amount || 0) : ocr.amount;
    if (!(n >= 0 && n <= MAX_AMOUNT)) {
      toast({ title: tt("a.1ff949c0", "금액을 확인해 주세요."), status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (edited && ocr && !reason.trim()) {
      toast({ title: tt("a.b249618f", "달라진 이유를 적어 주세요."), status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitAmount(supabase, {
        submissionId: card.id,
        amount: n,
        edited,
        reason: edited ? (reason.trim() || (ocr ? null : tt("a.377d6a7e", "자동 읽기 실패"))) : null,
        attested,
        // 이월 달은 「월별 수익 현황」 문장에 동의했다 — 서버 attest_version 으로 구분(2026-10-13)
        attestVersion: carriedNote ? LEGAL_ATTEST.versionCarried : LEGAL_ATTEST.version,
      });
      setSubmitInfo(res && typeof res === "object" ? res : null);
      setStep("done");
      onSubmitted?.();
    } catch (err) {
      // 서버가 양식 때문에 거부하면(wrong_screen · not_final · row_missing) 사유를 그대로 보여 주고 다른 캡처를 받는다
      const msg = String(err?.message || "");
      const m = /^(wrong_screen|wrong_row|wrong_bar|not_final|row_missing):\s*(.+)$/s.exec(msg);
      if (m) {
        setProblem({ kind: m[1], block: true, title: tt("a.6d2b8e17", "이 캡처는 받을 수 없어요"), body: m[2] });
        setStep("bad");
      } else {
        toast({ title: tt("a.dc2d6631", "제출하지 못했어요"), description: err?.message, status: "error", duration: 4000, isClosable: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const choose = (key) => {
    setSituation(key);
    setStep(key === SITUATION.pending ? "wait" : "shoot");
  };
  const stepAt = step === "ask" || step === "wait" ? 1 : step === "shoot" ? 2 : 3;

  const handleClose = () => {
    if (step === "reading" || submitting) return;
    onClose?.();
  };

  const month = monthLabel(card?.revenue_month);
  const hasViews = card?.month_views !== null && card?.month_views !== undefined;
  const hasPrev = card?.prev_amount !== null && card?.prev_amount !== undefined;

  return (
    <Modal isOpen={!!isOpen} onClose={handleClose} motionPreset="slideInBottom" size={{ base: "full", md: "lg" }} isCentered={false}>
      <ModalOverlay />
      <ModalContent borderRadius={{ base: 0, md: "12px" }} mt={{ base: 0, md: "10vh" }} data-naver-revenue-sheet>
        <ModalHeader fontSize="md" fontWeight="800" pr={12}>
          {step === "done"
            ? tt("a.43f4c700", "제출 완료")
            : tt("a.914f9a9d", "{{v0}} 네이버 클립 수익 제출", { v0: month })}
        </ModalHeader>
        {step !== "reading" && !submitting && <ModalCloseButton />}
        <ModalBody pb={2}>
          {/* 파일 입력은 단계와 무관하게 늘 있어야 한다 — 「받을 수 없는 캡처」 단계의 「다른 캡처 고르기」도 같은 입력을 연다(2026-10-08 QA 에서 잡은 빈틈) */}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="naver-revenue-file" />
          {step !== "done" && <StepBar at={stepAt} />}

          {/* ── ① 상황 고르기 (2026-10-13) ── */}
          {step === "ask" && (
            <Box data-testid="naver-revenue-ask">
              {isRejected && card?.creator_message && (
                <Box bg="accent.50" border="1px solid" borderColor="accent.200" borderRadius="8px" px={3} py={2} mb={3} fontSize="sm" color="neutral.800">
                  <Text fontWeight="700" mb={0.5}>{tt("a.087fc1a4", "운영팀 확인 요청")}</Text>
                  <Text whiteSpace="pre-wrap">{card.creator_message}</Text>
                </Box>
              )}
              <NaverRowEquation card={card} mb={2} />
              <Text fontSize="sm" fontWeight="800" color="neutral.900" mb={3} lineHeight="1.5">
                {tt("a.nv14a01", "네이버 클립 앱 › 내 정보 › 수익 › 정산 완료 내역을 열어 보세요. {{v0}}에 번 돈은 큰 글씨 「{{v1}}」 줄에 있어요. 그 줄이 어떻게 보이나요?", { v0: month, v1: naverRowMonth(card) })}
                {" "}<GuideHint id="settle.naver-capture-screen" /><GuideHint id="settle.naver-deadline" />
              </Text>
              <Flex direction="column" gap={2}>
                {situationOptions(card?.revenue_month).map((o) => (
                  <Box
                    key={o.key}
                    as="button"
                    type="button"
                    textAlign="left"
                    bg="white"
                    border="1px solid"
                    borderColor="neutral.200"
                    borderRadius="10px"
                    px={3}
                    py={2.5}
                    _hover={{ borderColor: "brand.400", bg: "brand.50" }}
                    _focusVisible={{ outline: "2px solid", outlineColor: "brand.400" }}
                    onClick={() => choose(o.key)}
                    data-testid={`naver-revenue-situation-${o.key}`}
                  >
                    <Flex justify="space-between" align="center" gap={2}>
                      <Text fontSize="sm" fontWeight="700" color="neutral.900">{o.title}</Text>
                      <Box as="span" fontSize="10px" fontWeight="800" borderRadius="full" px={2} py={0.5} whiteSpace="nowrap"
                        bg={o.tone === "wait" ? "accent.50" : "brand.50"} color={o.tone === "wait" ? "accent.700" : "brand.700"}>{o.chip}</Box>
                    </Flex>
                    <Text fontSize="xs" color="neutral.600" mt={0.5}>{o.body}</Text>
                  </Box>
                ))}
              </Flex>
              {card?.channel_name && (
                <Text fontSize="xs" color="neutral.500" mt={3}>{tt("a.e3bd4bc8", "채널 {{v0}}", { v0: card.channel_name })}</Text>
              )}
            </Box>
          )}

          {/* ── ①-b 지급 예정이면 기다림 ── */}
          {step === "wait" && (
            <Box data-testid="naver-revenue-wait" bg="accent.50" border="1px solid" borderColor="accent.200" borderRadius="8px" px={3} py={3}>
              <Text fontSize="sm" fontWeight="800" color="neutral.900" mb={1}>{tt("a.nv13w01", "아직 네이버가 확정하지 않았어요")}</Text>
              <Text fontSize="sm" color="neutral.800" lineHeight="1.6">
                {tt("a.nv13w02", "「지급 예정」·「지급 산정 중」인 금액은 바뀔 수 있어서 받지 않아요. 네이버는 보통 매달 6~7일에 확정해요. 「정산 완료」로 바뀌면 정산 화면에서 다시 올려 주세요.")}
              </Text>
              {card?.deadline_at && (
                <Text fontSize="xs" color="neutral.600" mt={2}>{tt("a.nv13w03", "이 달 마감은 {{v0}}이에요.", { v0: formatDeadline(card.deadline_at) })}</Text>
              )}
            </Box>
          )}

          {/* ── ② 찍을 화면 ── */}
          {step === "shoot" && (
            <Box data-testid="naver-revenue-shoot" data-situation={situation}>
              <Text fontSize="sm" fontWeight="800" color="neutral.900" mb={2}>{tt("a.nv13h01", "네이버 클립 앱에서 이 화면을 찍어 주세요")}</Text>
              <Box as="ol" pl={5} mb={3} fontSize="xs" color="neutral.800" lineHeight="1.8">
                {situationChecklist(situation, card?.revenue_month).map((c) => <li key={c}>{c}</li>)}
              </Box>
              <Box mb={3}>
                <SituationFigure situation={situation} revenueMonth={card?.revenue_month} />
              </Box>
              {(hasViews || hasPrev) && (
                <Box bg="neutral.50" border="1px solid" borderColor="neutral.200" borderRadius="8px" px={3} py={2} mb={3} fontSize="xs" color="neutral.600">
                  {hasViews && <Text>{tt("a.7fae59f3", "이 달 우리 집계 조회수 {{v0}}", { v0: formatViews(card.month_views) })}</Text>}
                  {hasPrev && <Text>{tt("a.ace4f517", "지난달 제출 금액 {{v0}}", { v0: formatKRW(card.prev_amount) })}</Text>}
                </Box>
              )}
              <Button colorScheme="brand" w="100%" size="md" onClick={pickFile} data-testid="naver-revenue-pick">
                {tt("a.dc855631", "캡처 이미지 고르기")}
              </Button>
              <Text fontSize="10px" color="neutral.500" mt={1.5} textAlign="center">
                {tt("a.bdcc7359", "JPG·PNG·WEBP·HEIC · 10MB 이하")}
              </Text>
              <ContinueOnPhone cardId={card?.id} />
            </Box>
          )}

          {/* ── b) 읽는 중 ── */}
          {step === "reading" && (
            <Flex direction="column" align="center" py={6} gap={3}>
              {previewUrl && <Image src={previewUrl} alt="" maxH="180px" borderRadius="8px" border="1px solid" borderColor="neutral.200" />}
              <Flex align="center" gap={2} color="neutral.700" fontSize="sm">
                <Spinner size="sm" color="brand.500" />
                <Text>{tt("a.76b833b7", "읽는 중…")}</Text>
              </Flex>
            </Flex>
          )}

          {/* ── b-2) 받을 수 없는 캡처 (2026-10-08 양식 강제) ── */}
          {step === "bad" && problem && (
            <Box data-testid="naver-revenue-bad" data-naver-revenue-problem={problem.kind}>
              <Flex gap={3} align="flex-start" mb={3}>
                {previewUrl && <Image src={previewUrl} alt="" w="72px" h="72px" objectFit="cover" borderRadius="8px" border="1px solid" borderColor="status.error" flexShrink={0} />}
                <Box flex={1} minW={0} bg="statusWash.error" border="1px solid" borderColor="statusLine.error" borderRadius="8px" px={3} py={2.5}>
                  <Flex align="center" gap={1.5} mb={1}>
                    <WarningTwoIcon color="status.error" boxSize="14px" />
                    <Text fontSize="sm" fontWeight="800" color="neutral.900">{problem.title}</Text>
                  </Flex>
                  <Text fontSize="xs" color="neutral.800" lineHeight="1.6" whiteSpace="pre-wrap">{problem.body}</Text>
                </Box>
              </Flex>
              <Text fontSize="xs" fontWeight="700" color="neutral.900" mb={2}>{tt("a.4c8e2d7f", "이 화면을 이렇게 찍어 주세요")}</Text>
              <SituationFigure situation={problem.kind === "wrong_row" || (problem.kind === "wrong_screen" && situation !== SITUATION.carried) ? SITUATION.settled : situation} revenueMonth={card?.revenue_month} />
            </Box>
          )}

          {/* ── c) 읽은 결과 ── */}
          {step === "result" && (
            <Box>
              {carriedNote && (
                <Box bg="accent.50" border="1px solid" borderColor="accent.200" borderRadius="8px" px={3} py={2.5} mb={3} data-testid="naver-revenue-carried-note">
                  <Text fontSize="sm" fontWeight="800" color="neutral.900" mb={1}>{carriedNote.title}</Text>
                  <Text fontSize="xs" color="neutral.800" lineHeight="1.6">{carriedNote.body}</Text>
                </Box>
              )}
              <Flex gap={3} align="flex-start" mb={3}>
                {previewUrl && <Image src={previewUrl} alt="" w="72px" h="72px" objectFit="cover" borderRadius="8px" border="1px solid" borderColor="neutral.200" flexShrink={0} />}
                <Box flex={1} minW={0}>
                  {ocr ? (
                    <>
                      <Text fontSize="xs" color="neutral.500" mb={1}>{tt("a.ba1b1086", "캡처에서 이렇게 읽었어요")}</Text>
                      <Row label={tt("a.72f8409b", "금액")} value={formatKRW(ocr.amount)} />
                      <Row
                        label={tt("a.7bc7c5c4", "기간")}
                        value={ocr.periodFrom || ocr.periodTo ? `${fmtDate(ocr.periodFrom)} ~ ${fmtDate(ocr.periodTo)}` : ""}
                      />
                      <Row label={tt("a.f0c83980", "채널")} value={ocr.channelLabel} />
                    </>
                  ) : (
                    <Box bg="accent.50" border="1px solid" borderColor="accent.200" borderRadius="8px" px={3} py={2} fontSize="sm" color="neutral.800">
                      {tt("a.3a13cade", "자동으로 읽지 못했어요 — 금액을 직접 입력해 주세요")}
                    </Box>
                  )}
                </Box>
              </Flex>

              {mode === "edit" && (
                <Box mt={2}>
                  <Text fontSize="xs" fontWeight="700" color="neutral.800" mb={1}>{tt("a.97d852c2", "{{v0}} 네이버 클립 수익 금액", { v0: month })}</Text>
                  <AmountInput value={amount} onChange={setAmount} autoFocus />
                  <Text fontSize="xs" fontWeight="700" color="neutral.800" mt={3} mb={1}>
                    {ocr ? tt("a.c18e72ee", "달라진 이유 (필수)") : tt("a.23ee555e", "메모 (선택)")}
                  </Text>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    fontSize="sm"
                    placeholder={ocr ? tt("a.3451704c", "예: 캡처가 지난달 화면이에요 / 금액 자릿수를 잘못 읽었어요") : tt("a.0fe28e72", "운영팀에 남길 말이 있으면 적어 주세요")}
                  />
                </Box>
              )}
              {/* 허위 기재 책임 고지 — 제출 직전 동의 필수 (대표 지시 2026-09-22 · 문안 v1 법무 확인 전 · 원본 lib LEGAL_ATTEST) */}
              <Box mt={3} bg="accent.50" border="1px solid" borderColor="accent.200" borderRadius="8px" px={3} py={2.5} data-testid="naver-revenue-attest">
                <Text fontSize="xs" fontWeight="800" color="neutral.900" mb={1}>{LEGAL_ATTEST.title} <GuideHint id="settle.naver-attest" /></Text>
                <Box as="ul" pl={4} fontSize="11px" color="neutral.700" lineHeight="1.6" mb={2}>
                  {LEGAL_ATTEST.lines.map((l) => <li key={l}>{l}</li>)}
                </Box>
                <Checkbox size="sm" colorScheme="brand" isChecked={attested} onChange={(e) => setAttested(e.target.checked)} alignItems="flex-start">
                  <Text fontSize="xs" fontWeight="700" color="neutral.900" lineHeight="1.5" data-testid="naver-revenue-attest-text">{carriedNote ? LEGAL_ATTEST.checkboxCarried : LEGAL_ATTEST.checkbox}</Text>
                </Checkbox>
              </Box>
              <Text fontSize="10px" color="neutral.500" mt={3}>
                {tt("a.86372062", "제출하면 운영팀이 캡처와 금액을 확인한 뒤 확정해요. 다른 캡처로 바꾸려면 닫고 다시 올려 주세요.")}
              </Text>
              {readError && !ocr && (
                <Text fontSize="10px" color="neutral.400" mt={1}>{tt("a.92ff398e", "읽기 실패 사유: {{v0}}", { v0: String(readError).slice(0, 80) })}</Text>
              )}
            </Box>
          )}

          {/* ── d) 완료 ── */}
          {step === "done" && (
            <Flex direction="column" align="center" py={6} gap={3} textAlign="center">
              <CheckCircleIcon boxSize="36px" color="status.success" />
              <Text fontSize="md" fontWeight="800" color="neutral.900">{tt("a.a19dd8d3", "제출했어요")}</Text>
              <Text fontSize="sm" color="neutral.600">{tt("a.d61ae5bd", "운영팀 확인 뒤 확정 알림을 보내요")}</Text>
              {Number(submitInfo?.carried_prev_amount) > 0 && (
                <Text fontSize="xs" color="neutral.600" data-testid="naver-revenue-r10-note">{tt("a.4e3045e3", "지난달 이월분 {{v0}}은 레이블리가 빼고 접수해요. 운영팀이 확인한 뒤 확정돼요.", { v0: formatKRW(submitInfo.carried_prev_amount) })}</Text>
              )}
              {(view?.kind === "overdue" || isRejected) && (
                <Text fontSize="xs" color="neutral.500">{tt("a.7bd0e065", "이제 레이블리를 다시 이용할 수 있어요.")}</Text>
              )}
            </Flex>
          )}
        </ModalBody>

        <ModalFooter gap={2} pt={2}>
          {step === "bad" && (
            <>
              <Button variant="ghost" color="neutral.500" onClick={handleClose}>
                {tt("a.a26db980", "나중에")}
              </Button>
              <Button colorScheme="brand" flex={1} onClick={pickFile} data-testid="naver-revenue-pick-again">
                {tt("a.5e2c9a07", "다른 캡처 고르기")}
              </Button>
            </>
          )}
          {step === "result" && ocr && mode === "confirm" && (
            <>
              <Button variant="outline" flex={1} onClick={() => { setMode("edit"); setAmount(String(ocr.amount)); }} isDisabled={submitting}>
                {tt("a.33924d0b", "달라요")}
              </Button>
              <Button colorScheme="brand" flex={1} isLoading={submitting} isDisabled={!attested} onClick={() => doSubmit({ edited: false })}>
                {tt("a.79dda0d5", "맞아요")}
              </Button>
            </>
          )}
          {step === "result" && mode === "edit" && (
            <>
              {ocr && (
                <Button variant="ghost" onClick={() => setMode("confirm")} isDisabled={submitting}>
                  {tt("a.9576e83e", "돌아가기")}
                </Button>
              )}
              <Button colorScheme="brand" flex={1} isLoading={submitting} isDisabled={amount === "" || !attested} onClick={() => doSubmit({ edited: true })}>
                {tt("a.e7152c62", "이 금액으로 제출")}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button colorScheme="brand" w="100%" onClick={() => onClose?.()}>
              {tt("a.218e2ae6", "닫기")}
            </Button>
          )}
          {step === "ask" && (
            <Button variant="ghost" color="neutral.500" onClick={handleClose}>
              {tt("a.a26db980", "나중에")}
            </Button>
          )}
          {(step === "shoot" || step === "wait") && (
            <Button variant="ghost" onClick={() => setStep("ask")} data-testid="naver-revenue-back">
              {tt("a.9576e83e", "돌아가기")}
            </Button>
          )}
          {step === "wait" && (
            <Button colorScheme="brand" flex={1} onClick={() => onClose?.()}>
              {tt("a.218e2ae6", "닫기")}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
